/**
 * Types for the temporary Native-to-Canvas migration console.
 *
 * Aligned with the Salesforce Canvas Migration API Specification
 * (PUT/GET /partners/:partner_id/salesforce-canvas-migrations).
 */

/** Values written to `crm_schema.canvas_migration_status`. */
export const CANVAS_MIGRATION_DB_STATUSES = [
  'pending',
  'in_progress',
  'completed',
] as const

export type CanvasMigrationDbStatus =
  (typeof CANVAS_MIGRATION_DB_STATUSES)[number]

export const MIGRATION_STATUSES = [
  'pending',
  'in_progress',
  'success',
  'failed',
] as const

export type MigrationStatus = (typeof MIGRATION_STATUSES)[number]

/** Spec error object on Job or Task; null when there is no error. */
export type CanvasMigrationError = {
  code: string
  message: string
} | null

/** Phase progress for the current phase only (resets when phase changes). */
export type CanvasMigrationPhaseProgress = {
  percentage: number
  processed_rows?: number
  total_rows?: number
} | null

/** Raw task object as returned by the migration control plane. */
export type CanvasMigrationTaskResponse = {
  task_id: string
  type: string
  name: string
  account_id: string | null
  status: MigrationStatus
  phase: string | null
  phase_progress: CanvasMigrationPhaseProgress
  progress: number
  error: CanvasMigrationError
  created_at: string
  updated_at: string
}

/** Raw job object as returned by the migration control plane. */
export type CanvasMigrationJobResponse = {
  job_id: string
  partner_id: string
  status: MigrationStatus
  progress: number
  error: CanvasMigrationError
  created_at: string
  updated_at: string
  tasks: CanvasMigrationTaskResponse[]
}

/** Normalised task used by the UI. */
export type MigrationTaskView = {
  taskId: string
  type: string
  name: string
  accountId: string
  status: MigrationStatus
  phase: string
  phaseLabel: string
  phasePercentage: number
  processedRows: number | null
  totalRows: number | null
  progress: number
  errorMessage: string
  errorCode: string
  /** ISO timestamp from the API. Display order is fixed to this value. */
  createdAt: string
}

/** Normalised job used by the UI. */
export type CanvasMigrationJobView = {
  jobId: string
  partnerId: string
  status: MigrationStatus
  progress: number
  errorMessage: string
  errorCode: string
  tasks: MigrationTaskView[]
  /** True once the backend has created a job we can render cards for. */
  hasJob: boolean
  /** True while the job is still running, which drives polling. */
  isRunning: boolean
  /** True when the job or any of its tasks needs a retry. */
  hasFailure: boolean
}

/** Which of the two console screens is currently visible. */
export type CanvasMigrationView = 'landing' | 'progress'

/**
 * True when at least one migration task has left the pre-start state.
 * Used with the DB status so Start Migration is not shown after work began.
 */
export const hasAnyTaskStarted = (job: CanvasMigrationJobView): boolean =>
  job.hasJob && job.tasks.some(task => task.status !== 'pending')

/**
 * True when the GET payload has at least one task and every task succeeded.
 * Job-level `success` alone is not enough — a lingering failed/running task
 * must not flip `crm_schema.canvas_migration_status` to completed.
 */
export const areAllMigrationTasksSuccessful = (
  job: CanvasMigrationJobView
): boolean =>
  job.hasJob &&
  job.tasks.length > 0 &&
  job.tasks.every(task => task.status === 'success')

/** Whether the partner should leave overview for the migration console. */
export const isCanvasMigrationExperienceActive = (
  status: string | null | undefined
): boolean => status === 'pending' || status === 'in_progress'
